<template>
    <div class="p-12 container">
        <h2>{{ addingToOrder ? 'Choose SSL Certificate' : 'SSL Certificate Options' }}</h2>

        <div class="flex flex-wrap" v-if="products">
            <sau-loading class="w-full" slot="Adding your certificate" v-if="loading"></sau-loading>

            <div v-else class="pb-8" :class="{
                'w-full': addingToOrder,
                'w-2/3 pr-8': ! addingToOrder,
            }">
                <ul class="breadcrumb bg-grey-100 rounded p-4 mb-8">
                    <li class="breadcrumb-item"><router-link to="/order">Order</router-link></li>
                    <template v-if="addingToOrder">
                        <li class="breadcrumb-item active">SSL Certificates</li>
                    </template>
                    <template v-else>
                        <li class="breadcrumb-item"><a href="#" @click.prevent="reset()">SSL Certificate</a></li>
                        <li class="breadcrumb-item active">Options</li>
                    </template>
                </ul>

                <template v-if="addingToOrder">
                    <div class="flex flex-wrap -m-4">
                        <div class="p-4 w-full sm:w-1/2 lg:w-1/3 xl:w-1/4" v-for="(option, index) in products" :key="index">
                            <div class="text-center p-8 border h-full flex flex-col content-between">
                                <!-- <h2 class="h4 w-3/4 mx-auto mb-8">{{ option.name }}</h2> -->
                                <div v-html="stripBreakTags(option.description)" class="mb-6"></div>
                                <div class="pt-2 mt-auto">
                                    <p class="block text-xs">Per Year Ex. GST<br>
                                    <span class="text-success text-2xl">{{ option.annual_price | currency}}</span></p>
                                    <button class="btn btn-success block w-full" @click="selectSsl(option.product_id)">Select</button>
                                </div>
                            </div>
                        </div>
                        <!-- <div class="p-4 w-full">
                            <lets-talk class="p-8 bg-white h-full"></lets-talk>
                        </div> -->
                    </div>
                </template>

                <div class="mb-4" v-if="! addingToOrder && selectedSsl">
                    <div class="sau-bx text-center p-5 mb-5">
                        <h2 class="text-2xl">{{ selectedSsl.name ? selectedSsl.name : '' }}</h2>
                    </div>

                    <div class="mb-5">
                        <h4>Domain Name</h4>
                        <p>Which domain do you wish to secure? For multi-domain certificates, enter any of the domains you'll be securing.</p>
                        <input
                            id="domain"
                            type="text"
                            v-model="domain"
                            @input="validateFqdn()"
                            @blur="updateSsl()"
                            class="form-control"
                            :class="{ 'is-invalid': domainError }"
                            placeholder="example.com"
                            v-on:keyup.enter="updateSsl()"
                            :disabled="! product_id || addingToOrder"
                        />
                        <div v-if="domainError" class="invalid-feedback">{{ domainError }}</div>
                    </div>

                    <div class="mb-5 mt-12">
                        <h4>Billing Cycle</h4>
                        <label v-if="selectedSsl.annual_price > 0">
                            <input type="radio" name="billing_cycle_years" v-model="billing_cycle_years" value="1" @change="updateSsl()" class="mr-2">
                            1 year - {{ selectedSsl.annual_price | currency }} Ex. GST
                        </label><br>
                        <label v-if="selectedSsl.biennial_price > 0">
                            <input type="radio" name="billing_cycle_years" v-model="billing_cycle_years" value="2" @change="updateSsl()" class="mr-2">
                            2 years - {{ selectedSsl.biennial_price | currency }} Ex. GST
                        </label>
                    </div>
                </div>
            </div>

            <sau-ordering-sidebar v-if="! addingToOrder" :data="data" :order="order"></sau-ordering-sidebar>
        </div>
    </div>
</template>

<script>
import OrderSummary from './Summary';
import LetsTalk from './partials/LetsTalk';

export default {
    props: {
        data: Object,
        order: Object,
    },

    components: {
        LetsTalk,
        OrderSummary,
    },

    data: function () {
        return {
            products: {},
            type: 'ssl-certificate',
            slug: null,
            domain: '',
            billing_cycle_years: 1,
            product_id: null,
            loading: false,
            addingToOrder: true,
            domainError: null,
        }
    },

    mounted: function () {
        this.reset();
        let initial = true;

        this.products = this.data.config.products.sslcertificate;
        const featuredProducts = [
            this.products.find(p => p.name === 'RapidSSL'),
            this.products.find(p => p.name === 'RapidSSL Wildcard'),
            this.products.find(p => p.name === 'GeoTrust True BusinessID'),
            this.products.find(p => p.name === 'GeoTrust True BusinessID + EV'),
        ].filter(p => p);
        // Featured products, followed by remaining products sorted by price:
        this.products = featuredProducts.concat(
            // Products sorted by price, excluding the featured products.
            this.products.sort((a, b) => a.annual_price - b.annual_price).filter(product => {
                return featuredProducts.map(featured => featured.id).includes(product.id) == false;
            })
        );

        // If user is returning to a SSL Certificate Order
        if (this.data.active.sslCertificate) {
            this.addingToOrder = false;
            // Set up the state to match the order
            this.setItemToEdit(this.order.items.find(
                item => item.type === this.type && item.slug === this.data.active.sslCertificate
            ));

            initial = false;
        }

        window.bus.$on('order.items.delete', (item) => {
            if (item.slug === this.slug) {
                this.reset();
            }
        });

        window.bus.$on('order.items.delete', (item) => {
            if (item.slug === this.slug) {
                this.reset();
            }
        });
        this.validateFqdn(initial);
    },

    methods: {
        reset() {
            this.domain = null;
            this.activeItem = null;
            this.product_id = null;
            this.addingToOrder = true;
            this.billing_cycle_years = 1;
        },

        setItemToEdit(item) {
            this.activeItem = item;
            this.addingToOrder = false;
            this.slug = item && item.type === 'ssl-certificate' ? item.slug : null;
            this.domain = item && item.type === 'ssl-certificate' ? item.domain : null;
            this.product_id = item && item.type === 'ssl-certificate' ? item.product_id : null;
            this.billing_cycle_years = item && item.type === 'ssl-certificate' ? item.billing_cycle_years : 1;
            this.data.active.sslCertificate = item && item.type === 'ssl-certificate' ? item.slug : false;

            if (! item) {
                this.reset();
            }
        },

        selectSsl(productId) {
            this.loading = true;
            this.addingToOrder = true;

            let product = this.products.find(p => p.product_id == productId);

            if (! product) {
                this.loading = false;
                this.addingToOrder = false;
                return;
            }

            this.domain = null;
            this.product_id = productId;
            this.billing_cycle_years = 1;

            let itemData = {
                product_id: productId,
                billing_cycle_years: 1,
            };

            
            window.axios.post(`/api/orders/${this.order.slug}/ssl-certificate/items`, itemData)
                .then(response => {
                    this.order.items.push(response.data)
                    this.slug = response.data.slug;
                }).finally(() => {
                    this.loading = false;
                    this.addingToOrder = false;
                });
        },

        updateSsl() {
            this.validateFqdn();
            let itemData = {
                domain: this.domain,
                product_id: this.product_id,
                billing_cycle_years: this.billing_cycle_years,
            };

            window.bus.$emit('order.items.update', 'ssl-certificate', this.slug, itemData);
        },

        validateFqdn(initial = false) {
            if (!initial) {
                const fqdnRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,6}$/;
                if (!fqdnRegex.test(this.domain)) {
                    this.domainError = 'Please enter a valid fully qualified domain name (e.g., example.com).';
                } else {
                    this.domainError = null;
                }
            }
        },

        stripBreakTags(subject) {
            return subject
                .replace(/\<br\>/g, '')
                .replace(/\<br\/\>/g, '')
                .replace(/\<br \/\>/g, '');
        },
    },

    computed: {
        selectedSsl() {
            return this.products.find(product => product.product_id == this.product_id);
        },
    },
}
</script>
